import { Box, Chip, Divider, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";

function InfoRow({ icon, label, value }) {
  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} sx={{ py: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          bgcolor: "#F3F4F6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: "0.72rem", display: "block" }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: "#111827", fontWeight: 600, fontSize: "0.88rem", lineHeight: 1.4 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function DetailCard(props) {
  const { t } = useTranslation();

  const statusColors = {
    PENDING:   { bg: "#FEF3C7", color: "#D97706" },
    ACCEPTED:  { bg: "#EFF6FF", color: "#2563EB" },
    PREPARED:  { bg: "#F5F3FF", color: "#7C3AED" },
    ASSIGNED:  { bg: "#ECFEFF", color: "#0891B2" },
    PICKED:    { bg: "#EEF2FF", color: "#4F46E5" },
    DELIVERED: { bg: "#F0FDF4", color: "#16A34A" },
    CANCELLED: { bg: "#FEF2F2", color: "#DC2626" },
  };
  const sc = statusColors[props.orderStatus] || { bg: "#F9FAFB", color: "#6B7280" };

  const formattedDate = props.createdAt
    ? new Date(props.createdAt).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid #F3F4F6",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        overflow: "hidden",
        mb: { xs: 2, md: 0 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          bgcolor: "#F9FAFB",
          borderBottom: "1px solid #F3F4F6",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#111827" }}>
          {t("orderDetail")}
        </Typography>
        <Chip
          label={props.orderStatus === "DELIVERED" && props.isPickedUp ? "PICKED UP" : props.orderStatus}
          size="small"
          sx={{
            bgcolor: sc.bg,
            color: sc.color,
            fontWeight: 700,
            fontSize: "0.68rem",
            height: 22,
            border: `1px solid ${sc.color}`,
          }}
        />
      </Box>

      <Box sx={{ px: 2.5, py: 1 }}>
        <InfoRow
          icon={<StoreOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />}
          label={t("orderFrom")}
          value={props.restaurant?.name ?? "—"}
        />
        <Divider sx={{ my: 0 }} />

        <InfoRow
          icon={<TagOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />}
          label={t("orderNo")}
          value={`#${props.orderId ?? "—"}`}
        />
        <Divider sx={{ my: 0 }} />

        <InfoRow
          icon={<LocationOnOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />}
          label={props.isPickedUp ? "Pick Up Address" : t("deliveryAddress")}
          value={
            props.isPickedUp
              ? props.restaurant?.address ?? "—"
              : props.deliveryAddress?.deliveryAddress ?? "—"
          }
        />
        <Divider sx={{ my: 0 }} />

        <InfoRow
          icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />}
          label="Order Placed"
          value={formattedDate}
        />
        {props.instructions && (
          <>
            <Divider sx={{ my: 0 }} />
            <InfoRow
              icon={<StickyNote2OutlinedIcon sx={{ fontSize: 16, color: "#6B7280" }} />}
              label="Delivery Instructions"
              value={props.instructions}
            />
          </>
        )}
      </Box>
    </Paper>
  );
}

export default DetailCard;
